import { Box, Chip, Tooltip, Typography } from '@mui/material';
import React from 'react';
import {
  PropagationOutcome,
  PropagationStatus as Status,
  ReferralPropagationState,
} from '../../../interfaces/referral';

/**
 * Where a referral edit actually got to.
 *
 * leads-api writes the referral first and delivers to LeadSquared, eligibility and core
 * afterwards, recording each outcome. Before that, a delivery that failed was either an
 * aborted update or a swallowed log line — either way the operator saw a green save and had
 * no way to know the CRM was still wrong. This is the panel that makes it knowable.
 */
export const PROPAGATION_TARGETS: ReadonlyArray<{
  key: keyof ReferralPropagationState;
  label: string;
}> = [
  { key: 'crm', label: 'LeadSquared' },
  { key: 'eligibility', label: 'Eligibility' },
  { key: 'core', label: 'Core' },
];

const PRESENTATION: Record<Status, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  synced: { label: 'synced', color: 'success' },
  pending: { label: 'pending', color: 'warning' },
  failed: { label: 'failed', color: 'error' },
  // Not a problem to fix: there was nothing for this target in the edit.
  skipped: { label: 'not needed', color: 'default' },
};

/** Mirrors `MAX_PROPAGATION_ATTEMPTS` in leads-api: past this the sweeper stops retrying. */
const MAX_ATTEMPTS = 5;

/**
 * A human summary of one delivery.
 *
 * Deliberately not the stored `error` string. That is whatever LeadSquared or eligibility
 * sent back, and an upstream error can echo another lead's email, phone or member details —
 * rendering it would show that to everyone who opens this card. What an operator needs is
 * whether the sweeper is still retrying or someone has to step in, and that follows from
 * the status and the attempt count alone.
 */
const describe = (outcome: PropagationOutcome, discarded: boolean): string => {
  const when = outcome.at ? new Date(outcome.at).toLocaleString() : 'unknown time';
  const attempts = outcome.attempts === 1 ? '1 attempt' : `${outcome.attempts} attempts`;

  if (outcome.status === 'failed') {
    // The sweeper skips discarded referrals, so promising a retry there would be false.
    const next = discarded
      ? 'not retried, the referral was discarded'
      : outcome.attempts >= MAX_ATTEMPTS
        ? 'retries exhausted, needs someone to look'
        : 'will be retried automatically';
    return `${when} · ${attempts} — ${next}`;
  }

  // `reason` on a skipped target is written by leads-api itself ("no linked core account
  // yet"), not relayed from a third party, so it is safe and useful to show.
  return [`${when} · ${attempts}`, outcome.status === 'skipped' ? outcome.reason : null]
    .filter(Boolean)
    .join(' — ');
};

interface PropagationStatusProps {
  propagation?: ReferralPropagationState;
  /** A discarded referral is never swept again, which changes what a failure means. */
  discarded?: boolean;
}

const PropagationStatus: React.FC<PropagationStatusProps> = ({ propagation, discarded = false }) => {
  // Referrals edited before the ledger existed have none. Showing three grey chips would
  // suggest nothing was delivered, which is not what an empty ledger means.
  // Keyed on the targets this build knows, not on the object being non-empty: a ledger with
  // only unrecognised keys would otherwise render the label with no chips beneath it.
  if (!propagation || !PROPAGATION_TARGETS.some(({ key }) => propagation[key])) {
    return null;
  }

  const failures = PROPAGATION_TARGETS.flatMap(({ key, label }) => {
    const outcome = propagation[key];
    return outcome?.status === 'failed' ? [{ key, label, outcome }] : [];
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
        LAST EDIT REACHED
      </Typography>
      {PROPAGATION_TARGETS.map(({ key, label }) => {
        const outcome = propagation[key];

        if (!outcome) {
          return null;
        }

        // An unrecognised status is shown as what it is, not as "pending" — calling a state the UI
        // does not understand "pending" tells the operator to wait for something that may not come.
        const { label: statusLabel, color } = PRESENTATION[outcome.status] ?? {
          label: String(outcome.status),
          color: 'default' as const,
        };

        return (
          <Tooltip key={key} title={describe(outcome, discarded)}>
            <Chip size='small' color={color} variant='outlined' label={`${label}: ${statusLabel}`} />
          </Tooltip>
        );
      })}
      {failures.length > 0 && (
        // Visible text, not only the tooltip. For a failed target, whether the sweeper will
        // retry or has given up is what decides whether to wait or step in, and a hover-only
        // tooltip put that out of reach of keyboard, touch and screen readers.
        <Box sx={{ flexBasis: '100%' }}>
          {failures.map(({ key, label, outcome }) => (
            <Typography key={key} variant='caption' color='error' display='block'>
              {label}: {describe(outcome, discarded)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PropagationStatus;
